/**
 * Order Queue badge visibility: the known badge keys, their defaults, and the
 * resolver from the user's saved display settings (profiles.ui_preferences).
 *
 * The key list lives here, not in @threadheads/shared: the server stores
 * `orderQueue.badges` as an open string-to-boolean record, so a new key never
 * needs a backend deploy. Unknown keys in stored settings are ignored.
 *
 * Defaults are Brett's 2026-10-07 answer: every badge on except "Portless label
 * ready". Hybrid parcel stays on because it is the row's only warning that the
 * item count is not the whole parcel.
 */
import type { UiPreferences } from '@threadheads/shared';

export const ORDER_QUEUE_BADGE_KEYS = [
  'fulfilmentType',
  'holdCountdown',
  'resolved',
  'archivedSku',
  'portlessLabelNotReady',
  'portlessLabelReady',
  'hybrid',
] as const;

export type OrderQueueBadgeKey = (typeof ORDER_QUEUE_BADGE_KEYS)[number];
export type OrderQueueBadgeVisibility = Record<OrderQueueBadgeKey, boolean>;

export const ORDER_QUEUE_BADGE_DEFAULTS: OrderQueueBadgeVisibility = Object.freeze({
  fulfilmentType: true,
  holdCountdown: true,
  resolved: true,
  archivedSku: true,
  portlessLabelNotReady: true,
  portlessLabelReady: false,
  hybrid: true,
});

/** Defaults overlaid with the user's saved booleans for the known keys only. */
export function resolveBadgeVisibility(
  prefs: UiPreferences | null | undefined,
): OrderQueueBadgeVisibility {
  const saved = prefs?.orderQueue?.badges;
  const out: OrderQueueBadgeVisibility = { ...ORDER_QUEUE_BADGE_DEFAULTS };
  if (!saved || typeof saved !== 'object') return out;
  for (const key of ORDER_QUEUE_BADGE_KEYS) {
    const value = (saved as Record<string, unknown>)[key];
    if (typeof value === 'boolean') out[key] = value;
  }
  return out;
}
